// Ekran 02 drawer'ları.
//   1. seviye: "Numune al"  ·  "Reddet"  ·  "Storno"
//   2. seviye: "Yeni cari — hızlı kayıt"  (1. seviye .pushed olur, kapanmaz)

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DrawerAltlik, DrawerGovde, useDrawer } from '../../components/Drawer'
import { toast } from '../../components/Toast'
import { ApiHatasi } from '../../lib/api'
import { trTarihSaat } from '../../lib/trFormat'
import { numuneler } from '../lab/api'
import { cariler as cariApi } from '../masterdata/api'
import type { LotOzet } from './api'

/* ------------------------------------------------------------ numune al */

export function NumuneDrawer({ lot }: { lot: LotOzet }) {
  const { kapat } = useDrawer()
  const kuyruk = useQueryClient()
  const [not, setNot] = useState('')

  const { data: sonraki } = useQuery({
    queryKey: ['numune-sonraki-no'],
    queryFn: numuneler.sonrakiNo,
  })

  const kaydet = useMutation({
    mutationFn: () => numuneler.olustur({ tip: 'lot', lotId: lot.id }),
    onSuccess: (n) => {
      kuyruk.invalidateQueries({ queryKey: ['lotlar'] })
      kuyruk.invalidateQueries({ queryKey: ['lot'] })
      kuyruk.invalidateQueries({ queryKey: ['lot-ozeti'] })
      kuyruk.invalidateQueries({ queryKey: ['numuneler'] })
      kuyruk.invalidateQueries({ queryKey: ['numune-sonraki-no'] })
      toast(`${n.no} oluşturuldu → Laboratuvar kuyruğuna kaydedildi`)
      kapat()
    },
    onError: (h) => toast(h instanceof ApiHatasi ? h.message : 'Numune oluşturulamadı'),
  })

  return (
    <>
      <DrawerGovde>
        <div className="info-box">
          <div>
            Numune, lota bağlanır ve <b>Laboratuvar</b> kuyruğuna düşer. Analiz sonucu
            geldiğinde havuz kalitesi otomatik güncellenir (K13).
          </div>
        </div>

        <div className="fgrid" style={{ marginTop: 10 }}>
          <div className="fld">
            <label>Numune no</label>
            <input className="in mono" readOnly value={sonraki?.no ?? '…'} />
            <div className="fld-hint">Numara sunucu sayacından gelir.</div>
          </div>
          <div className="fld">
            <label>Alınma zamanı</label>
            <input className="in mono" readOnly value={trTarihSaat(new Date())} />
          </div>
          <div className="fld" style={{ gridColumn: '1/-1' }}>
            <label>Not</label>
            <input
              className="in"
              placeholder="—"
              value={not}
              onChange={(e) => setNot(e.target.value)}
            />
          </div>
          <div className="fld" style={{ gridColumn: '1/-1' }}>
            <button
              className="btn btn-sec btn-sm"
              type="button"
              style={{ width: '100%' }}
              disabled
              title="Etiket yazdırma henüz bağlanmadı"
              onClick={() => undefined}
            >
              Etiket yazdır
            </button>
          </div>
        </div>
      </DrawerGovde>

      <DrawerAltlik>
        <button className="btn btn-sec" type="button" onClick={() => kapat()}>
          Vazgeç
        </button>
        <button
          className="btn btn-pri"
          type="button"
          disabled={kaydet.isPending}
          onClick={() => kaydet.mutate()}
        >
          {kaydet.isPending ? 'Kaydediliyor…' : 'Numuneyi kaydet'}
        </button>
      </DrawerAltlik>
    </>
  )
}

/* --------------------------------------------------- gerekçeli işlemler */

/**
 * Red ve storno aynı kalıbı paylaşıyor: ikisi de gerekçe zorunlu ve ikisi de
 * geri alınamaz. Referansta red gerekçesi sabit bir metindi
 * ("Görsel kontrol sonucu reddedildi"); sunucu gerekçe istiyor (409), o yüzden
 * kullanıcıya soruluyor.
 */
export function GerekceDrawer({
  baslik,
  uyari,
  butonEtiketi,
  tehlikeli = false,
  bekliyor,
  onay,
}: {
  baslik: string
  uyari: React.ReactNode
  butonEtiketi: string
  tehlikeli?: boolean
  bekliyor: boolean
  onay: (gerekce: string) => void
}) {
  const { kapat } = useDrawer()
  const [gerekce, setGerekce] = useState('')

  return (
    <>
      <DrawerGovde>
        <div
          className="info-box"
          style={{
            background: 'var(--amber-bg)',
            borderColor: 'var(--amber-bd)',
            color: 'var(--amber)',
          }}
        >
          <div>{uyari}</div>
        </div>

        <div className="fld" style={{ marginTop: 10 }}>
          <label>{baslik}</label>
          <input
            className="in"
            value={gerekce}
            onChange={(e) => setGerekce(e.target.value)}
            autoFocus
          />
          <div className="fld-hint">Denetim izine bu metin yazılır.</div>
        </div>
      </DrawerGovde>

      <DrawerAltlik>
        <button className="btn btn-sec" type="button" onClick={() => kapat()}>
          Vazgeç
        </button>
        <button
          className={tehlikeli ? 'btn btn-danger' : 'btn btn-pri'}
          type="button"
          disabled={gerekce.trim() === '' || bekliyor}
          title={gerekce.trim() === '' ? 'Gerekçe zorunlu' : undefined}
          onClick={() => {
            onay(gerekce.trim())
            kapat()
          }}
        >
          {bekliyor ? 'İşleniyor…' : butonEtiketi}
        </button>
      </DrawerAltlik>
    </>
  )
}

/* ------------------------------------------- 2. seviye: hızlı yeni cari */

/**
 * Referansın vaadi: "Mevcut kabul formunuz açık kalır". Drawer 2. seviyede
 * açılır, 1. seviye <c>.pushed</c> alır ve KAPANMAZ; kayıt sonrası yeni cari
 * açık formda seçili gelir.
 */
export function HizliCariDrawer({ secildi }: { secildi: (cariId: string) => void }) {
  const { kapat } = useDrawer()
  const kuyruk = useQueryClient()
  const [unvan, setUnvan] = useState('')
  const [vergiNo, setVergiNo] = useState('')
  const [rol, setRol] = useState<'atik' | 'tedarikci' | 'nakliyeci'>('atik')

  const kaydet = useMutation({
    mutationFn: () =>
      cariApi.kaydet(null, {
        unvan: unvan.trim(),
        vergiNo: vergiNo.trim() || null,
        rolAtik: rol === 'atik',
        rolTedarikci: rol === 'tedarikci',
        rolNakliyeci: rol === 'nakliyeci',
      }),
    onSuccess: (c) => {
      kuyruk.invalidateQueries({ queryKey: ['cariler'] })
      secildi(c.id)
      toast(`“${c.unvan}” eklendi ve seçildi — kabul formunuz açık`)
      kapat()
    },
    onError: (h) => toast(h instanceof ApiHatasi ? h.message : 'Cari eklenemedi'),
  })

  return (
    <>
      <DrawerGovde>
        <div className="info-box">
          <div>
            Kaydedince bu cari, açık formdaki cari alanına <b>otomatik seçili</b> gelir.
          </div>
        </div>

        <div className="fgrid" style={{ marginTop: 10 }}>
          <div className="fld" style={{ gridColumn: '1/-1' }}>
            <label>Unvan</label>
            <input
              className="in"
              value={unvan}
              onChange={(e) => setUnvan(e.target.value)}
              autoFocus
            />
          </div>
          <div className="fld">
            <label>Tip</label>
            <select
              className="in"
              value={rol}
              onChange={(e) => setRol(e.target.value as typeof rol)}
            >
              <option value="atik">Atık üreticisi</option>
              <option value="tedarikci">Tedarikçi</option>
              <option value="nakliyeci">Nakliyeci</option>
            </select>
          </div>
          <div className="fld">
            <label>VKN</label>
            <input
              className="in mono"
              value={vergiNo}
              onChange={(e) => setVergiNo(e.target.value)}
            />
          </div>
        </div>
      </DrawerGovde>

      <DrawerAltlik>
        <button className="btn btn-sec" type="button" onClick={() => kapat()}>
          Vazgeç
        </button>
        <button
          className="btn btn-pri"
          type="button"
          disabled={unvan.trim() === '' || kaydet.isPending}
          onClick={() => kaydet.mutate()}
        >
          {kaydet.isPending ? 'Kaydediliyor…' : 'Kaydet ve seç'}
        </button>
      </DrawerAltlik>
    </>
  )
}
